import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, Clock3, Heart, LogIn, Menu, Search, Settings, UserRound, X } from 'lucide-react'
import { siteConfig } from '../data/content'
import { useMuseumStore } from '../store/useMuseumStore'

const nav = [
  ['/', '首页'], ['/about', '走进博物馆'], ['/history', '历史记忆'], ['/digital', '数字展馆'], ['/vr', 'VR体验'], ['/artifacts', '文物展示'], ['/news', '新闻活动'], ['/education', '教育资源'], ['/service', '开放服务'],
]

export default function Layout() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { profile, isLoggedIn, logout, toast, showToast } = useMuseumStore()

  useEffect(() => {
    setMenuOpen(false); setAccountOpen(false)
  }, [pathname])
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 36)
    const onOutside = (e: MouseEvent) => accountRef.current && !accountRef.current.contains(e.target as Node) && setAccountOpen(false)
    window.addEventListener('scroll', onScroll); document.addEventListener('mousedown', onOutside)
    return () => { window.removeEventListener('scroll', onScroll); document.removeEventListener('mousedown', onOutside) }
  }, [])

  const submitSearch = (value: string) => {
    if (!value.trim()) return
    navigate(`/artifacts?q=${encodeURIComponent(value.trim())}`); setSearchOpen(false)
  }

  return <div className="site-shell">
    <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
      <Link className="brand" to="/" aria-label="九一八历史博物馆首页">
        <span className="brand-mark"><b>9·18</b><i>1931</i></span>
        <span className="brand-name">沈阳九一八<br/><em>历史博物馆</em></span>
      </Link>
      <nav className={`main-nav ${menuOpen ? 'open' : ''}`} aria-label="主导航">
        {nav.map(([path, label]) => <NavLink key={path} to={path} end={path === '/'}>{label}</NavLink>)}
        <NavLink className="mobile-only" to="/profile">个人中心</NavLink>
        <NavLink className="mobile-only" to="/settings">设置</NavLink>
      </nav>
      <div className="header-actions">
        <button className="icon-btn" onClick={() => setSearchOpen(!searchOpen)} aria-label="打开全站搜索"><Search size={19}/></button>
        <div className="account-wrap" ref={accountRef}>
          <button className="account-button" onClick={() => setAccountOpen(!accountOpen)} aria-expanded={accountOpen} aria-haspopup="menu">
            <Avatar avatar={profile.avatar} name={profile.username} />
            <span>{isLoggedIn ? profile.username : '游客'}</span><ChevronDown size={14}/>
          </button>
          {accountOpen && <div className="account-menu" role="menu">
            {isLoggedIn ? <>
              <div className="account-summary"><Avatar avatar={profile.avatar} name={profile.username}/><div><strong>{profile.username}</strong><small>{profile.email || '尚未绑定邮箱'}</small></div></div>
              <Link role="menuitem" to="/profile"><UserRound/>个人中心</Link>
              <Link role="menuitem" to="/profile?tab=favorites"><Heart/>我的收藏</Link>
              <Link role="menuitem" to="/profile?tab=history"><Clock3/>浏览历史</Link>
              <Link role="menuitem" to="/settings"><Settings/>设置</Link>
              <button role="menuitem" onClick={() => { logout(); showToast('已安全退出登录'); navigate('/') }}><LogIn/>退出登录</button>
            </> : <>
              <p className="account-guest">登录后可收藏文物、报名活动并保存学习记录。</p>
              <Link className="menu-primary" role="menuitem" to="/auth"><LogIn/>登录 / 注册</Link>
            </>}
          </div>}
        </div>
        <Link className="settings-link" to="/settings" aria-label="设置"><Settings size={19}/></Link>
        <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label="切换导航菜单">{menuOpen ? <X/> : <Menu/>}</button>
      </div>
      {searchOpen && <form className="global-search" onSubmit={(e) => { e.preventDefault(); submitSearch(new FormData(e.currentTarget).get('q') as string) }}>
        <Search size={20}/><input name="q" autoFocus aria-label="搜索关键词" placeholder="搜索文物、历史事件或教育资源…"/><button>搜索</button>
      </form>}
    </header>
    <main id="main-content"><Outlet /></main>
    <footer className="site-footer">
      <div className="footer-brand"><span className="brand-mark"><b>9·18</b><i>1931</i></span><div><strong>{siteConfig.name}</strong><p>铭记历史 · 缅怀先烈 · 珍爱和平 · 开创未来</p></div></div>
      <div><h4>快速访问</h4><Link to="/about">参观指南</Link><Link to="/digital">数字展馆</Link><Link to="/education">教育资源</Link><Link to="/messages">留言互动</Link></div>
      <div><h4>联系信息</h4><p>{siteConfig.address}</p><p>{siteConfig.phone}</p><p>{siteConfig.email}</p></div>
      <div><h4>项目说明</h4><p>高校暑期社会实践数字展示项目</p><p>史料与馆藏演示内容待馆方审校授权</p></div>
      <p className="copyright">© 2026 九一八历史博物馆数字实践项目 · 本站为教学演示，非官方站点</p>
    </footer>
    {toast && <div className="toast" role="status">{toast}</div>}
    <button className="back-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="返回顶部">↑</button>
  </div>
}

export function Avatar({ avatar, name, large = false }: { avatar: string; name: string; large?: boolean }) {
  return <span className={`avatar ${large ? 'large' : ''}`}>{avatar ? <img src={avatar} alt={`${name}的头像`}/> : <span>{(name || '游').slice(0, 1).toUpperCase()}</span>}</span>
}
